import React, { useState, useEffect } from "react";
import styled from 'styled-components/macro';
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { ResponseT } from "@utils/api/base";
import { FlexRow, FlexItem } from '@components/layout/FlexStyles';
import { TextTiny } from '@components/elements/TextStyles';
import {
  ListContainer,
  ListError,
  ListLoader,
} from "@components/elements/listStyles";
import { RootState } from "../../store";
import useSubmissions from "./useSubmissions";
import { SubmissionL } from "../../type";
import qs from "qs";
import BasicTable from '@components/composite/BasicTable';
import { tableFields, getTableRows } from '@components/submissions/SubmissionList.utils';
import MenuButton from '@components/controls/MenuButton';
import ContinueIcon from '@components/icons/continue.icon';

const debug = false;
const SubmissionsList = () => {
  const { getSubmissions } = useSubmissions();
  const [refreshList, setRefreshList] = useState<boolean>(false);
  const submissions = useSelector((state: RootState) => state.submissions);
  const navigate = useNavigate();
  const location = useLocation();

  // ###########################################################################
  // INFINITE LOADING
  // ###########################################################################
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadedAll, setLoadedAll] = useState(false);

  debug && console.log("SubmissionList | submissions:", submissions);

  const handleDetails = (e, uuid) => {
    e.stopPropagation();
    navigate(`/submissions/${uuid}`)
  }

  const areSubmissionsPending = () => {
    debug && console.log("areSubmissionsPending | data", data);
    if (data?.submissionList) {
      const pending = data?.submissionList.filter(
        (s) => s.status === "pending"
      );
      if (pending.length) {
        return true;
      }
    }
    return false;
  }

  const getPendingInformation = () => {
    if (!areSubmissionsPending()) return null;
    return(
      <FlexRow v_centered>
        <FlexItem>
          <TextTiny color={'accent'}>
            Submissions are in process. This table will auto-refresh every 10s until they are done.
          </TextTiny>
        </FlexItem>
        <FlexItem>
          <MenuButton
            icon={<ContinueIcon size={24}/>}
            text={'Manual Refresh'}
            tooltipPlacement={'bottom-end'}
            onClick = {()=>setRefreshList(true)}
          />
        </FlexItem>
      </FlexRow>
    )
  }

  const handleAutoRefreshIfPending = () => {
    debug &&
      console.log(
        "handleAutoRefreshIfPending pendingSubmissions:",
        areSubmissionsPending(),
        "loading:",
        loading
      );
    if (areSubmissionsPending() && !loading) {
      debug && console.log("Setting refresh");
      setRefreshList(true);
    }
  };

  // to allow user to refresh if they are impatient.
  const handleRefresh = () => {
    setRefreshList(true);
  };

  const loadData = React.useCallback(async () => {
    setLoading(true);
    let res: ResponseT<SubmissionL>;

    const params: { [propName: string]: any } = {
      take: 10,
      skip: 0,
    };

    res = await getSubmissions(params);
    debug && console.log("getSubmissions | res: ", res);

    setData(res);
    setLoading(false);
    // setLoadedAll(!res.continues);
  }, []);

  const loadMore = async () => {
    setLoadingMore(true);
    let res: ResponseT<SubmissionL>;

    const params: { [propName: string]: any } = {
      start: 1,
      limit: 50,
    };

    res = await getSubmissions(params);
    setData([...data, res?.data]);
    setLoading(false);
  };

  const handleEndReached = () => {
    if (loading || loadingMore || loadedAll) return;
    loadMore().then();
  };

  // Load data on specific conditions
  useEffect(() => {
    if (submissions && loading === false) {
      setRefreshList(false);
      loadData().then();
    }
  }, [submissions, loadData, refreshList]);

  // Set up an interval to refresh data every 5 seconds if there are pending submissions
  useEffect(() => {
    // Set up an interval to refresh data every 5 seconds (for example)
    const intervalId = setInterval(handleAutoRefreshIfPending, 10000);
    // Clean up the interval when the component is unmounted
    return () => clearInterval(intervalId);
  }, [data, loading]);

  // ###########################################################################
  // Interactions
  // ###########################################################################
  const handleClick = ({ rowKey }) => {
    //console.log('Click DATA', rowKey);
    const search = {
      ...qs.parse(location.search, { ignoreQueryPrefix: true }),
    };
    navigate({
      ...location,
      pathname: `/detail/${rowKey}`,
      search: qs.stringify(search),
    });
  };

  const [sortBy, setSortBy] = React.useState({});

  debug && console.log("SubmissionList | pendingSubmissions", areSubmissionsPending());
  debug && console.log(data);
  return (
    <ListContainer>
      {!loading ? (
        <TableContainer>
          { getPendingInformation() }
          <BasicTable fields={tableFields} rows={getTableRows(data?.submissionList, handleDetails)} />
        </TableContainer>
      ) : (
        <>
          {" "}
          {loading ? (
            <FlexRow width={'100%'} height={'200px'} v_centered h_centered>
              <ListLoader />
            </FlexRow>
          ) : (
            <ListError>
              Missing Submissions or the filter settings did not return any
              submissions
            </ListError>
          )}
        </>
      )}
    </ListContainer>
  );
};

export default SubmissionsList;

const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

